import React, { useState, useEffect } from 'react'
import { 
  ArrowLeft,
  Save,
  RotateCcw,
  DollarSign,
  Clock,
  Users,
  Layers,
  CreditCard,
  Target,
  Settings,
  Plus,
  X,
  Calendar
} from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import pricingModelMasterService from '../../services/pricingModelMasterService'
import RevisionModal from '../../components/RevisionModal/RevisionModal'
import '../../components/RevisionModal/RevisionModal.css'

const PricingModelMasterEdit = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [activeTab, setActiveTab] = useState('basicInfo')
  const [pricingModelData, setPricingModelData] = useState(null)
  const [formData, setFormData] = useState({
    pricingModelCode: '',
    pricingModelName: '',
    pricingModelDescription: '',
    pricingType: 'Time & Material',
    pricingModelStatus: 'Active',
    isDefault: false,
    pricingConfiguration: {
      baseRate: 0,
      currency: 'INR',
      billingUnit: 'Hour',
      minimumBilling: 0,
      maximumBilling: 0,
      discountStructure: 'None',
      discountPercentage: 0,
    },
    rateStructure: {
      hourlyRates: [],
      dailyRates: [],
      projectRates: []
    },
    billingSettings: {
      billingFrequency: 'Monthly',
      paymentTerms: 'Net 30',
      latePaymentFee: 0,
      requiresAdvancePayment: false,
      advancePaymentPercentage: 0,
      invoicingMethod: 'Manual'
    },
    applicabilitySettings: {
      applicableProjectTypes: [],
      applicableIndustries: [],
      minimumProjectDuration: 1,
      maximumProjectDuration: 365,
      minimumProjectValue: 0,
      maximumProjectValue: 10000000,
      requiresClientApproval: false
    },
    performanceMetrics: {
      averageProjectMargin: 0,
      averageProjectDuration: 0,
      clientSatisfactionScore: 0,
      repeatClientRate: 0,
      utilizationRate: 0
    },
    visualSettings: {
      color: '#222448',
      icon: 'DollarSign',
      sortOrder: 1
    }
  })
  const [loading, setLoading] = useState(true)
  const [showRevisionModal, setShowRevisionModal] = useState(false)
  const [pendingFormData, setPendingFormData] = useState(null)

  useEffect(() => {
    if (location.state?.pricingModelData) {
      const { pricingModelData } = location.state
      setPricingModelData(pricingModelData)
      setFormData({
        pricingModelCode: pricingModelData.pricingModelCode || '',
        pricingModelName: pricingModelData.pricingModelName || '',
        pricingModelDescription: pricingModelData.pricingModelDescription || '',
        pricingType: pricingModelData.pricingType || 'Time & Material',
        pricingModelStatus: pricingModelData.pricingModelStatus || 'Active',
        isDefault: pricingModelData.isDefault || false,
        pricingConfiguration: {
          baseRate: pricingModelData.pricingConfiguration?.baseRate || 0,
          currency: pricingModelData.pricingConfiguration?.currency || 'INR',
          billingUnit: pricingModelData.pricingConfiguration?.billingUnit || 'Hour',
          minimumBilling: pricingModelData.pricingConfiguration?.minimumBilling || 0,
          maximumBilling: pricingModelData.pricingConfiguration?.maximumBilling || 0,
          discountStructure: pricingModelData.pricingConfiguration?.discountStructure || 'None',
          discountPercentage: pricingModelData.pricingConfiguration?.discountPercentage || 0,
        },
        rateStructure: {
          hourlyRates: pricingModelData.rateStructure?.hourlyRates || [],
          dailyRates: pricingModelData.rateStructure?.dailyRates || [],
          projectRates: pricingModelData.rateStructure?.projectRates || []
        },
        billingSettings: {
          billingFrequency: pricingModelData.billingSettings?.billingFrequency || 'Monthly',
          paymentTerms: pricingModelData.billingSettings?.paymentTerms || 'Net 30',
          latePaymentFee: pricingModelData.billingSettings?.latePaymentFee || 0,
          requiresAdvancePayment: pricingModelData.billingSettings?.requiresAdvancePayment || false,
          advancePaymentPercentage: pricingModelData.billingSettings?.advancePaymentPercentage || 0,
          invoicingMethod: pricingModelData.billingSettings?.invoicingMethod || 'Manual'
        },
        applicabilitySettings: {
          applicableProjectTypes: pricingModelData.applicabilitySettings?.applicableProjectTypes || [],
          applicableIndustries: pricingModelData.applicabilitySettings?.applicableIndustries || [],
          minimumProjectDuration: pricingModelData.applicabilitySettings?.minimumProjectDuration || 1,
          maximumProjectDuration: pricingModelData.applicabilitySettings?.maximumProjectDuration || 365,
          minimumProjectValue: pricingModelData.applicabilitySettings?.minimumProjectValue || 0,
          maximumProjectValue: pricingModelData.applicabilitySettings?.maximumProjectValue || 10000000,
          requiresClientApproval: pricingModelData.applicabilitySettings?.requiresClientApproval || false
        },
        performanceMetrics: {
          averageProjectMargin: pricingModelData.performanceMetrics?.averageProjectMargin || 0,
          averageProjectDuration: pricingModelData.performanceMetrics?.averageProjectDuration || 0,
          clientSatisfactionScore: pricingModelData.performanceMetrics?.clientSatisfactionScore || 0,
          repeatClientRate: pricingModelData.performanceMetrics?.repeatClientRate || 0,
          utilizationRate: pricingModelData.performanceMetrics?.utilizationRate || 0
        },
        visualSettings: {
          color: pricingModelData.visualSettings?.color || '#222448',
          icon: pricingModelData.visualSettings?.icon || 'DollarSign',
          sortOrder: pricingModelData.visualSettings?.sortOrder || 1
        }
      })
      setLoading(false)
    } else {
      toast.error('No Pricing Model data provided for editing')
      navigate('/projects/pricing-model-master')
    }
  }, [location.state, navigate])

  const pricingTypes = [
    'Time & Material',
    'Fixed Price', 
    'Staff Augmentation',
    'Hybrid',
    'Retainer',
    'Milestone-based'
  ]

  const currencies = ['INR', 'USD', 'EUR', 'GBP', 'CAD', 'AUD']
  const billingUnits = ['Hour', 'Day', 'Week', 'Month', 'Project', 'Milestone']
  const discountStructures = ['None', 'Volume', 'Loyalty', 'Early Payment', 'Custom']
  const billingFrequencies = ['Weekly', 'Bi-weekly', 'Monthly', 'Quarterly', 'Milestone-based', 'Project-based']
  const paymentTerms = ['Net 15', 'Net 30', 'Net 45', 'Net 60', 'Due on Receipt', 'Custom']
  const invoicingMethods = ['Manual', 'Automated', 'Semi-automated']

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.')
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }))
    }
  }

  const handleArrayChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.')
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }))
    }
  }

  const addArrayItem = (field, newItem) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.')
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: [...prev[parent][child], newItem || '']
        }
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: [...prev[field], newItem || '']
      }))
    }
  }

  const removeArrayItem = (field, index) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.')
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: prev[parent][child].filter((_, i) => i !== index)
        }
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: prev[field].filter((_, i) => i !== index)
      }))
    }
  }

  const addRateItem = (rateType, newItem) => {
    setFormData(prev => ({
      ...prev,
      rateStructure: {
        ...prev.rateStructure,
        [rateType]: [...prev.rateStructure[rateType], newItem || { role: '', rate: 0, currency: 'INR' }]
      }
    }))
  }

  const removeRateItem = (rateType, index) => {
    setFormData(prev => ({
      ...prev,
      rateStructure: {
        ...prev.rateStructure,
        [rateType]: prev.rateStructure[rateType].filter((_, i) => i !== index)
      }
    }))
  }

  const updateRateItem = (rateType, index, field, value) => {
    setFormData(prev => ({
      ...prev,
      rateStructure: {
        ...prev.rateStructure,
        [rateType]: prev.rateStructure[rateType].map((item, i) => 
          i === index ? { ...item, [field]: value } : item
        )
      }
    }))
  }

  const validateForm = () => {
    if (!formData.pricingModelCode.trim()) {
      toast.error('Pricing Model Code is required')
      return false
    }
    if (!/^[A-Z0-9-]+$/.test(formData.pricingModelCode.trim())) {
      toast.error('Pricing Model Code must contain only uppercase letters, numbers, and hyphens')
      return false
    }
    if (!formData.pricingModelName.trim()) {
      toast.error('Pricing Model Name is required')
      return false
    }
    if (!formData.pricingModelDescription.trim()) {
      toast.error('Pricing Model Description is required')
      return false
    }
    if (formData.pricingConfiguration.baseRate < 0) {
      toast.error('Base Rate cannot be negative')
      return false
    }
    if (formData.pricingConfiguration.discountPercentage < 0 || formData.pricingConfiguration.discountPercentage > 100) {
      toast.error('Discount Percentage must be between 0 and 100')
      return false
    }
    return true
  }

  const handleSave = async () => {
    if (!validateForm()) {
      return
    }

    // Check if there are changes
    const hasChanges = JSON.stringify(formData) !== JSON.stringify({
      pricingModelCode: pricingModelData.pricingModelCode || '',
      pricingModelName: pricingModelData.pricingModelName || '',
      pricingModelDescription: pricingModelData.pricingModelDescription || '',
      pricingType: pricingModelData.pricingType || 'Time & Material',
      pricingModelStatus: pricingModelData.pricingModelStatus || 'Active',
      isDefault: pricingModelData.isDefault || false,
      pricingConfiguration: pricingModelData.pricingConfiguration || {
        baseRate: 0,
        currency: 'INR',
        billingUnit: 'Hour',
        minimumBilling: 0,
        maximumBilling: 0,
        discountStructure: 'None',
        discountPercentage: 0,
      },
      rateStructure: pricingModelData.rateStructure || {
        hourlyRates: [],
        dailyRates: [],
        projectRates: []
      },
      billingSettings: pricingModelData.billingSettings || {
        billingFrequency: 'Monthly',
        paymentTerms: 'Net 30',
        latePaymentFee: 0,
        requiresAdvancePayment: false,
        advancePaymentPercentage: 0,
        invoicingMethod: 'Manual'
      },
      applicabilitySettings: pricingModelData.applicabilitySettings || {
        applicableProjectTypes: [],
        applicableIndustries: [],
        minimumProjectDuration: 1,
        maximumProjectDuration: 365,
        minimumProjectValue: 0,
        maximumProjectValue: 10000000,
        requiresClientApproval: false
      },
      performanceMetrics: pricingModelData.performanceMetrics || {
        averageProjectMargin: 0,
        averageProjectDuration: 0,
        clientSatisfactionScore: 0,
        repeatClientRate: 0,
        utilizationRate: 0
      },
      visualSettings: pricingModelData.visualSettings || {
        color: '#222448',
        icon: 'DollarSign',
        sortOrder: 1
      }
    })

    if (hasChanges) {
      setPendingFormData(formData)
      setShowRevisionModal(true)
    } else {
      toast.info('No changes detected')
    }
  }

  const handleRevisionSave = async (revisionData) => {
    try {
      setLoading(true)
      const response = await pricingModelMasterService.updatePricingModelMaster(
        pricingModelData._id, 
        pendingFormData
      )
      
      if (response.success) {
        toast.success('Pricing model updated successfully')
        navigate('/projects/pricing-model-master')
      } else {
        toast.error(response.message || 'Failed to update pricing model')
      }
    } catch (error) {
      console.error('Error updating pricing model:', error)
      toast.error('Error updating pricing model')
    } finally {
      setLoading(false)
      setShowRevisionModal(false)
      setPendingFormData(null)
    }
  }

  const handleReset = () => {
    if (pricingModelData) {
      setFormData({
        pricingModelCode: pricingModelData.pricingModelCode || '',
        pricingModelName: pricingModelData.pricingModelName || '',
        pricingModelDescription: pricingModelData.pricingModelDescription || '',
        pricingType: pricingModelData.pricingType || 'Time & Material',
        pricingModelStatus: pricingModelData.pricingModelStatus || 'Active',
        isDefault: pricingModelData.isDefault || false,
        pricingConfiguration: {
          baseRate: pricingModelData.pricingConfiguration?.baseRate || 0,
          currency: pricingModelData.pricingConfiguration?.currency || 'INR',
          billingUnit: pricingModelData.pricingConfiguration?.billingUnit || 'Hour',
          minimumBilling: pricingModelData.pricingConfiguration?.minimumBilling || 0,
          maximumBilling: pricingModelData.pricingConfiguration?.maximumBilling || 0,
          discountStructure: pricingModelData.pricingConfiguration?.discountStructure || 'None',
          discountPercentage: pricingModelData.pricingConfiguration?.discountPercentage || 0,
        },
        rateStructure: {
          hourlyRates: pricingModelData.rateStructure?.hourlyRates || [],
          dailyRates: pricingModelData.rateStructure?.dailyRates || [],
          projectRates: pricingModelData.rateStructure?.projectRates || []
        },
        billingSettings: {
          billingFrequency: pricingModelData.billingSettings?.billingFrequency || 'Monthly',
          paymentTerms: pricingModelData.billingSettings?.paymentTerms || 'Net 30',
          latePaymentFee: pricingModelData.billingSettings?.latePaymentFee || 0,
          requiresAdvancePayment: pricingModelData.billingSettings?.requiresAdvancePayment || false,
          advancePaymentPercentage: pricingModelData.billingSettings?.advancePaymentPercentage || 0,
          invoicingMethod: pricingModelData.billingSettings?.invoicingMethod || 'Manual'
        },
        applicabilitySettings: {
          applicableProjectTypes: pricingModelData.applicabilitySettings?.applicableProjectTypes || [],
          applicableIndustries: pricingModelData.applicabilitySettings?.applicableIndustries || [],
          minimumProjectDuration: pricingModelData.applicabilitySettings?.minimumProjectDuration || 1,
          maximumProjectDuration: pricingModelData.applicabilitySettings?.maximumProjectDuration || 365,
          minimumProjectValue: pricingModelData.applicabilitySettings?.minimumProjectValue || 0,
          maximumProjectValue: pricingModelData.applicabilitySettings?.maximumProjectValue || 10000000,
          requiresClientApproval: pricingModelData.applicabilitySettings?.requiresClientApproval || false
        },
        performanceMetrics: {
          averageProjectMargin: pricingModelData.performanceMetrics?.averageProjectMargin || 0,
          averageProjectDuration: pricingModelData.performanceMetrics?.averageProjectDuration || 0,
          clientSatisfactionScore: pricingModelData.performanceMetrics?.clientSatisfactionScore || 0,
          repeatClientRate: pricingModelData.performanceMetrics?.repeatClientRate || 0,
          utilizationRate: pricingModelData.performanceMetrics?.utilizationRate || 0
        },
        visualSettings: {
          color: pricingModelData.visualSettings?.color || '#222448',
          icon: pricingModelData.visualSettings?.icon || 'DollarSign',
          sortOrder: pricingModelData.visualSettings?.sortOrder || 1
        }
      })
      toast.info('Form reset to original values')
    }
  }

  if (loading) {
    return (
      <div className="sales-dashboard">
        <div className="page-header">
          <div className="page-title-section">
            <h1>Loading...</h1>
          </div>
        </div>
      </div>
    )
  }

  const breadcrumbItems = [
    { label: 'Projects', path: '/projects', onClick: () => navigate('/projects') },
    { label: 'Masters', path: '/projects?tab=masters', onClick: () => navigate('/projects?tab=masters') },
    { label: 'Pricing Model Master', path: '/projects/pricing-model-master', onClick: () => navigate('/projects/pricing-model-master') },
    { label: 'Edit', path: '/projects/pricing-model-master/edit', isCurrent: true }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Pricing Model Master Entry</h1>
          <p>Update pricing model configuration and settings</p>
        </div>
      </div>

      {/* Form Section */}
      <div className="form-container">
        <div className="sac-form-header">
          <h2>Pricing Model Master Entry</h2>
        </div>

        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === 'basicInfo' ? 'active' : ''}`}
            onClick={() => setActiveTab('basicInfo')}
          >
            <DollarSign size={18} />
            <span>Basic Information</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'pricingConfig' ? 'active' : ''}`}
            onClick={() => setActiveTab('pricingConfig')}
          >
            <Settings size={18} />
            <span>Pricing Configuration</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'rateStructure' ? 'active' : ''}`}
            onClick={() => setActiveTab('rateStructure')}
          >
            <Clock size={18} />
            <span>Rate Structure</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'billingSettings' ? 'active' : ''}`}
            onClick={() => setActiveTab('billingSettings')}
          >
            <CreditCard size={18} />
            <span>Billing Settings</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'applicability' ? 'active' : ''}`}
            onClick={() => setActiveTab('applicability')}
          >
            <Target size={18} />
            <span>Applicability</span>
          </button>
        </div>

        <form className="form-content">

          {/* Basic Information Tab */}
          {activeTab === 'basicInfo' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Basic Information</h3>
              </div>
              
              {/* Row 1: Pricing Model Code, Name, Type, Status */}
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="pricingModelCode" className="form-label required">
                    Pricing Model Code
                  </label>
                  <input
                    id="pricingModelCode"
                    type="text"
                    value={formData.pricingModelCode}
                    onChange={(e) => handleInputChange('pricingModelCode', e.target.value.toUpperCase())}
                    className="form-input"
                    placeholder="e.g., TM-HOURLY, FIXED-PRICE"
                    maxLength={20}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="pricingModelName" className="form-label required">
                    Pricing Model Name
                  </label>
                  <input
                    id="pricingModelName"
                    type="text"
                    value={formData.pricingModelName}
                    onChange={(e) => handleInputChange('pricingModelName', e.target.value)}
                    className="form-input"
                    placeholder="e.g., Time & Material - Hourly"
                    maxLength={100}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="pricingType" className="form-label required">
                    Pricing Type
                  </label>
                  <select
                    id="pricingType"
                    value={formData.pricingType}
                    onChange={(e) => handleInputChange('pricingType', e.target.value)}
                    className="form-input"
                  >
                    <option value="">Select Pricing Type</option>
                    {pricingTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="pricingModelStatus" className="form-label">
                    Status
                  </label>
                  <select
                    id="pricingModelStatus"
                    value={formData.pricingModelStatus}
                    onChange={(e) => handleInputChange('pricingModelStatus', e.target.value)}
                    className="form-input"
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Archived">Archived</option>
                  </select>
                </div>
              </div>

              {/* Row 2: Description */}
              <div className="form-group">
                <label htmlFor="pricingModelDescription" className="form-label required">
                  Pricing Model Description
                </label>
                <textarea
                  id="pricingModelDescription"
                  value={formData.pricingModelDescription}
                  onChange={(e) => handleInputChange('pricingModelDescription', e.target.value)}
                  className="form-textarea"
                  placeholder="Describe the pricing model, its benefits, and when to use it..."
                  rows={4}
                />
              </div>

              {/* Row 3: Default Checkbox */}
              <div className="form-group">
                <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={formData.isDefault}
                    onChange={(e) => handleInputChange('isDefault', e.target.checked)}
                  />
                  <span>Set as Default Pricing Model</span>
                </label>
              </div>
            </div>
          )}

        {/* Other tabs would be identical to the create form */}
        {/* For brevity, I'm including the key tabs that show the structure */}
        
        {activeTab === 'pricingConfig' && (
          <div className="tab-content">
            <div className="section-header">
              <h3>Pricing Configuration</h3>
            </div>
            
            <div className="grid-row-4">
              <div className="form-group">
                <label htmlFor="baseRate" className="form-label">
                  Base Rate
                </label>
                <input
                  id="baseRate"
                  type="number"
                  value={formData.pricingConfiguration.baseRate}
                  onChange={(e) => handleInputChange('pricingConfiguration.baseRate', parseFloat(e.target.value) || 0)}
                  className="form-input"
                  min="0"
                  step="0.01"
                />
              </div>

              <div className="form-group">
                <label htmlFor="currency" className="form-label">
                  Currency
                </label>
                <select
                  id="currency"
                  value={formData.pricingConfiguration.currency}
                  onChange={(e) => handleInputChange('pricingConfiguration.currency', e.target.value)}
                  className="form-input"
                >
                  {currencies.map(currency => (
                    <option key={currency} value={currency}>{currency}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="billingUnit" className="form-label">
                  Billing Unit
                </label>
                <select
                  id="billingUnit"
                  value={formData.pricingConfiguration.billingUnit}
                  onChange={(e) => handleInputChange('pricingConfiguration.billingUnit', e.target.value)}
                  className="form-input"
                >
                  {billingUnits.map(unit => (
                    <option key={unit} value={unit}>{unit}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="discountStructure" className="form-label">
                  Discount Structure
                </label>
                <select
                  id="discountStructure"
                  value={formData.pricingConfiguration.discountStructure}
                  onChange={(e) => handleInputChange('pricingConfiguration.discountStructure', e.target.value)}
                  className="form-input"
                >
                  {discountStructures.map(structure => (
                    <option key={structure} value={structure}>{structure}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid-row-3">
              <div className="form-group">
                <label htmlFor="minimumBilling" className="form-label">
                  Minimum Billing
                </label>
                <input
                  id="minimumBilling"
                  type="number"
                  value={formData.pricingConfiguration.minimumBilling}
                  onChange={(e) => handleInputChange('pricingConfiguration.minimumBilling', parseFloat(e.target.value) || 0)}
                  className="form-input"
                  min="0"
                />
              </div>

              <div className="form-group">
                <label htmlFor="maximumBilling" className="form-label">
                  Maximum Billing
                </label>
                <input
                  id="maximumBilling"
                  type="number"
                  value={formData.pricingConfiguration.maximumBilling}
                  onChange={(e) => handleInputChange('pricingConfiguration.maximumBilling', parseFloat(e.target.value) || 0)}
                  className="form-input"
                  min="0"
                />
              </div>

              <div className="form-group">
                <label htmlFor="discountPercentage" className="form-label">
                  Discount Percentage
                </label>
                <input
                  id="discountPercentage"
                  type="number"
                  value={formData.pricingConfiguration.discountPercentage}
                  onChange={(e) => handleInputChange('pricingConfiguration.discountPercentage', parseFloat(e.target.value) || 0)}
                  className="form-input"
                  min="0"
                  max="100"
                  step="0.01"
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'rateStructure' && (
          <div className="tab-content">
            <div className="section-header">
              <h3>Rate Structure</h3>
            </div>
            
            {/* Hourly Rates */}
            <div className="form-group">
              <label className="form-label">Hourly Rates</label>
              <div className="rates-container" style={{
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                padding: '20px',
                marginBottom: '20px'
              }}>
                <div className="rates-header" style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '16px'
                }}>
                  <h4 style={{ 
                    margin: 0, 
                    fontSize: '16px', 
                    fontWeight: '600', 
                    color: '#374151',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <Clock size={18} style={{ color: '#3B82F6' }} />
                    Hourly Rate Configuration
                  </h4>
                  <button
                    type="button"
                    onClick={() => addRateItem('hourlyRates')}
                    className="btn-primary"
                    style={{
                      padding: '6px 12px',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <Plus size={14} />
                    Add Hourly Rate
                  </button>
                </div>

                {formData.rateStructure.hourlyRates.length === 0 ? (
                  <div className="empty-state" style={{
                    textAlign: 'center',
                    padding: '40px 20px',
                    color: '#64748b'
                  }}>
                    <Clock size={32} style={{ marginBottom: '12px', opacity: 0.5 }} />
                    <p style={{ margin: '0 0 12px 0', fontSize: '14px' }}>No hourly rates configured yet</p>
                    <p style={{ margin: 0, fontSize: '12px', opacity: 0.8 }}>Add hourly rates for different roles and expertise levels</p>
                  </div>
                ) : (
                  <div className="rates-list" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {formData.rateStructure.hourlyRates.map((rate, index) => (
                      <div key={index} className="rate-item" style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '16px',
                        backgroundColor: 'white',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                        transition: 'all 0.2s ease'
                      }}>
                        <div className="rate-number" style={{
                          width: '24px',
                          height: '24px',
                          backgroundColor: '#3B82F6',
                          color: 'white',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          fontWeight: '600',
                          flexShrink: 0
                        }}>
                          {index + 1}
                        </div>
                        
                        <div style={{ flex: 1 }}>
                          <input
                            type="text"
                            value={rate.role}
                            onChange={(e) => updateRateItem('hourlyRates', index, 'role', e.target.value)}
                            className="form-input"
                            placeholder="Role (e.g., Senior Developer, Tech Lead)"
                            style={{ 
                              margin: 0,
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              padding: '8px 12px',
                              fontSize: '14px'
                            }}
                          />
                        </div>
                        
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ position: 'relative' }}>
                            <input
                              type="number"
                              value={rate.rate}
                              onChange={(e) => updateRateItem('hourlyRates', index, 'rate', parseFloat(e.target.value) || 0)}
                              className="form-input"
                              placeholder="Rate"
                              min="0"
                              step="0.01"
                              style={{ 
                                width: '120px',
                                margin: 0,
                                border: '1px solid #d1d5db',
                                borderRadius: '6px',
                                padding: '8px 12px',
                                fontSize: '14px',
                                textAlign: 'right'
                              }}
                            />
                            <span style={{
                              position: 'absolute',
                              right: '12px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              fontSize: '12px',
                              color: 'var(--color-text-secondary, #5B6478)',
                              pointerEvents: 'none'
                            }}>/hr</span>
                          </div>
                          
                          <select
                            value={rate.currency}
                            onChange={(e) => updateRateItem('hourlyRates', index, 'currency', e.target.value)}
                            className="form-input"
                            style={{ 
                              width: '80px',
                              margin: 0,
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              padding: '8px 12px',
                              fontSize: '14px'
                            }}
                          >
                            {currencies.map(currency => (
                              <option key={currency} value={currency}>{currency}</option>
                            ))}
                          </select>
                          
                          <button
                            type="button"
                            onClick={() => removeRateItem('hourlyRates', index)}
                            style={{
                              padding: '8px 12px',
                              fontSize: '12px',
                              backgroundColor: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#dc2626'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#ef4444'}
                          >
                            <X size={14} />
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Daily Rates */}
            <div className="form-group">
              <label className="form-label">Daily Rates</label>
              <div className="rates-container" style={{
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                padding: '20px',
                marginBottom: '20px'
              }}>
                <div className="rates-header" style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '16px'
                }}>
                  <h4 style={{ 
                    margin: 0, 
                    fontSize: '16px', 
                    fontWeight: '600', 
                    color: '#374151',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <Calendar size={18} style={{ color: '#10B981' }} />
                    Daily Rate Configuration
                  </h4>
                  <button
                    type="button"
                    onClick={() => addRateItem('dailyRates')}
                    className="btn-primary"
                    style={{
                      padding: '6px 12px',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <Plus size={14} />
                    Add Daily Rate
                  </button>
                </div>

                {formData.rateStructure.dailyRates.length === 0 ? (
                  <div className="empty-state" style={{
                    textAlign: 'center',
                    padding: '40px 20px',
                    color: '#64748b'
                  }}>
                    <Calendar size={32} style={{ marginBottom: '12px', opacity: 0.5 }} />
                    <p style={{ margin: '0 0 12px 0', fontSize: '14px' }}>No daily rates configured yet</p>
                    <p style={{ margin: 0, fontSize: '12px', opacity: 0.8 }}>Add daily rates for different roles and expertise levels</p>
                  </div>
                ) : (
                  <div className="rates-list" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {formData.rateStructure.dailyRates.map((rate, index) => (
                      <div key={index} className="rate-item" style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '16px',
                        backgroundColor: 'white',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                        transition: 'all 0.2s ease'
                      }}>
                        <div className="rate-number" style={{
                          width: '24px',
                          height: '24px',
                          backgroundColor: '#10B981',
                          color: 'white',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          fontWeight: '600',
                          flexShrink: 0
                        }}>
                          {index + 1}
                        </div>
                        
                        <div style={{ flex: 1 }}>
                          <input
                            type="text"
                            value={rate.role}
                            onChange={(e) => updateRateItem('dailyRates', index, 'role', e.target.value)}
                            className="form-input"
                            placeholder="Role (e.g., Senior Developer, Tech Lead)"
                            style={{ 
                              margin: 0,
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              padding: '8px 12px',
                              fontSize: '14px'
                            }}
                          />
                        </div>
                        
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ position: 'relative' }}>
                            <input
                              type="number"
                              value={rate.rate}
                              onChange={(e) => updateRateItem('dailyRates', index, 'rate', parseFloat(e.target.value) || 0)}
                              className="form-input"
                              placeholder="Rate"
                              min="0"
                              step="0.01"
                              style={{ 
                                width: '120px',
                                margin: 0,
                                border: '1px solid #d1d5db',
                                borderRadius: '6px',
                                padding: '8px 12px',
                                fontSize: '14px',
                                textAlign: 'right'
                              }}
                            />
                            <span style={{
                              position: 'absolute',
                              right: '12px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              fontSize: '12px',
                              color: 'var(--color-text-secondary, #5B6478)',
                              pointerEvents: 'none'
                            }}>/day</span>
                          </div>
                          
                          <select
                            value={rate.currency}
                            onChange={(e) => updateRateItem('dailyRates', index, 'currency', e.target.value)}
                            className="form-input"
                            style={{ 
                              width: '80px',
                              margin: 0,
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              padding: '8px 12px',
                              fontSize: '14px'
                            }}
                          >
                            {currencies.map(currency => (
                              <option key={currency} value={currency}>{currency}</option>
                            ))}
                          </select>
                          
                          <button
                            type="button"
                            onClick={() => removeRateItem('dailyRates', index)}
                            style={{
                              padding: '8px 12px',
                              fontSize: '12px',
                              backgroundColor: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#dc2626'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#ef4444'}
                          >
                            <X size={14} />
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Project Rates */}
            <div className="form-group">
              <label className="form-label">Project Rates</label>
              <div className="rates-container" style={{
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                padding: '20px',
                marginBottom: '20px'
              }}>
                <div className="rates-header" style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '16px'
                }}>
                  <h4 style={{ 
                    margin: 0, 
                    fontSize: '16px', 
                    fontWeight: '600', 
                    color: '#374151',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <Target size={18} style={{ color: '#8B5CF6' }} />
                    Project Rate Configuration
                  </h4>
                  <button
                    type="button"
                    onClick={() => addRateItem('projectRates', { projectType: '', baseRate: 0, complexityMultiplier: 1.0 })}
                    className="btn-primary"
                    style={{
                      padding: '6px 12px',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <Plus size={14} />
                    Add Project Rate
                  </button>
                </div>

                {formData.rateStructure.projectRates.length === 0 ? (
                  <div className="empty-state" style={{
                    textAlign: 'center',
                    padding: '40px 20px',
                    color: '#64748b'
                  }}>
                    <Target size={32} style={{ marginBottom: '12px', opacity: 0.5 }} />
                    <p style={{ margin: '0 0 12px 0', fontSize: '14px' }}>No project rates configured yet</p>
                    <p style={{ margin: 0, fontSize: '12px', opacity: 0.8 }}>Add project rates with complexity multipliers</p>
                  </div>
                ) : (
                  <div className="rates-list" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {formData.rateStructure.projectRates.map((rate, index) => (
                      <div key={index} className="rate-item" style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '16px',
                        backgroundColor: 'white',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                        transition: 'all 0.2s ease'
                      }}>
                        <div className="rate-number" style={{
                          width: '24px',
                          height: '24px',
                          backgroundColor: '#8B5CF6',
                          color: 'white',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          fontWeight: '600',
                          flexShrink: 0
                        }}>
                          {index + 1}
                        </div>
                        
                        <div style={{ flex: 1 }}>
                          <input
                            type="text"
                            value={rate.projectType}
                            onChange={(e) => updateRateItem('projectRates', index, 'projectType', e.target.value)}
                            className="form-input"
                            placeholder="Project Type (e.g., Simple Website, E-commerce)"
                            style={{ 
                              margin: 0,
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              padding: '8px 12px',
                              fontSize: '14px'
                            }}
                          />
                        </div>
                        
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ position: 'relative' }}>
                            <input
                              type="number"
                              value={rate.baseRate}
                              onChange={(e) => updateRateItem('projectRates', index, 'baseRate', parseFloat(e.target.value) || 0)}
                              className="form-input"
                              placeholder="Base Rate"
                              min="0"
                              step="0.01"
                              style={{ 
                                width: '120px',
                                margin: 0,
                                border: '1px solid #d1d5db',
                                borderRadius: '6px',
                                padding: '8px 12px',
                                fontSize: '14px',
                                textAlign: 'right'
                              }}
                            />
                            <span style={{
                              position: 'absolute',
                              right: '12px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              fontSize: '12px',
                              color: 'var(--color-text-secondary, #5B6478)',
                              pointerEvents: 'none'
                            }}>₹</span>
                          </div>
                          
                          <div style={{ position: 'relative' }}>
                            <input
                              type="number"
                              value={rate.complexityMultiplier}
                              onChange={(e) => updateRateItem('projectRates', index, 'complexityMultiplier', parseFloat(e.target.value) || 1.0)}
                              className="form-input"
                              placeholder="Multiplier"
                              min="0.5"
                              max="3.0"
                              step="0.1"
                              style={{ 
                                width: '100px',
                                margin: 0,
                                border: '1px solid #d1d5db',
                                borderRadius: '6px',
                                padding: '8px 12px',
                                fontSize: '14px',
                                textAlign: 'right'
                              }}
                            />
                            <span style={{
                              position: 'absolute',
                              right: '8px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              fontSize: '12px',
                              color: 'var(--color-text-secondary, #5B6478)',
                              pointerEvents: 'none'
                            }}>x</span>
                          </div>
                          
                          <button
                            type="button"
                            onClick={() => removeRateItem('projectRates', index)}
                            style={{
                              padding: '8px 12px',
                              fontSize: '12px',
                              backgroundColor: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#dc2626'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#ef4444'}
                          >
                            <X size={14} />
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        </form>

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            onClick={handleReset}
            className="btn-secondary"
            disabled={loading}
          >
            <RotateCcw size={16} />
            Reset
          </button>
          
          <button
            type="button"
            onClick={handleSave}
            className="btn-primary"
            disabled={loading}
          >
            <Save size={16} />
            {loading ? 'Updating...' : 'Update Pricing Model'}
          </button>
        </div>
      </div>

      {/* Revision Modal */}
      <RevisionModal
        isOpen={showRevisionModal}
        onClose={() => setShowRevisionModal(false)}
        onSave={handleRevisionSave}
        currentRevision={pricingModelData?.currentRevision || 1}
      />
    </div>
  )
}

export default PricingModelMasterEdit
